import { useCallback, useEffect, useRef, useState } from "react";
import { adminApi } from "@/services/api/admin";
import { ApiErrorResponse } from "@/services/api/client";
import { teacherApi, type RadarBubble } from "@/services/api/teacher";
import type { RadarReadStage } from "./TalkRadarRead";
import type { RadarCardKind, RadarDot } from "./TalkRadarDisc";

export type TalkView = "home" | "list" | "mini";

export type RadarRead = {
  stage: RadarReadStage;
  requestId: string | null;
  fromList: boolean;
  requestIds: string[];
  kind: RadarCardKind;
  requestStatus: string | null;
  requestDate: string | null;
  assignLessonId?: string | null;
  assignSubstitute?: string | null;
  incoming?: boolean;
  personId?: string | null;
};

function radarLoadError(err: unknown): string {
  const status = err instanceof ApiErrorResponse ? err.statusCode : undefined;
  return `Radar konnte nicht geladen werden (${status || "Unbekannt"})`;
}

function aborted(err: unknown): boolean {
  return (
    typeof err === "object" &&
    err !== null &&
    ((err as { name?: string }).name === "CanceledError" ||
      (err as { name?: string }).name === "AbortError" ||
      (err as { code?: string }).code === "ERR_CANCELED")
  );
}

export function useTalkRadar(
  view: TalkView,
  setView: (next: TalkView) => void,
  role: string | undefined
) {
  const [radarRestoreView, setRadarRestoreView] = useState<TalkView | null>(
    null
  );
  const [radarBubbles, setRadarBubbles] = useState<RadarBubble[] | null>(null);
  const [radarVisibleIds, setRadarVisibleIds] = useState<string[] | null>(null);
  const [radarTimeDays, setRadarTimeDays] = useState<number | undefined>(
    undefined
  );
  const [radarError, setRadarError] = useState<string | null>(null);
  const [radarRead, setRadarRead] = useState<RadarRead | null>(null);
  const [radarMessage, setRadarMessage] = useState(false);
  const stornoBack = useRef<(() => void) | null>(null);
  const setStornoBack = useCallback((fn: (() => void) | null) => {
    stornoBack.current = fn;
  }, []);

  function openRadar() {
    setRadarBubbles(null);
    setRadarVisibleIds(null);
    setRadarError(null);
    setRadarRead(null);
    setRadarMessage(false);
    setRadarRestoreView(view);
  }

  useEffect(() => {
    if (radarRestoreView === null) return;
    if (role !== "admin" && role !== "teacher") return;
    let live = true;
    setRadarBubbles(null);
    setRadarVisibleIds(null);
    setRadarError(null);
    const load =
      role === "admin"
        ? adminApi.getAdminRadar(radarTimeDays)
        : teacherApi.getTeacherRadarBubbles(radarTimeDays);
    load
      .then((data) => {
        if (!live) return;
        const bubbles = (data.bubbles || []) as RadarBubble[];
        if (bubbles.length === 0) return;
        setRadarBubbles(bubbles);
      })
      .catch((err: unknown) => {
        if (!live || aborted(err)) return;
        setRadarError(radarLoadError(err));
        setRadarBubbles([]);
      });
    return () => {
      live = false;
    };
  }, [radarRestoreView, role, radarTimeDays]);

  function closeRadar() {
    if (radarRestoreView) setView(radarRestoreView);
    setRadarRestoreView(null);
    setRadarRead(null);
    setRadarMessage(false);
  }

  function personRequestIds(personId: string | null, fallback: string[]) {
    if (!personId || !radarBubbles) return fallback;
    const ids = radarBubbles.flatMap((bubble) => {
      if (bubble.requester_teacher_id !== personId) return [];
      if (bubble.request_ids && bubble.request_ids.length > 0) return bubble.request_ids;
      return bubble.request_id ? [bubble.request_id] : [];
    });
    const unique = [...new Set(ids)];
    return unique.length > 0 ? unique : fallback;
  }

  function openRadarDot(dot: RadarDot) {
    const personId = dot.personId;
    const requestIds =
      role === "admin" ? personRequestIds(personId, dot.requestIds) : dot.requestIds;
    if (role !== "admin" && requestIds.length > 1) {
      setRadarRead({
        stage: "list",
        requestId: null,
        fromList: false,
        requestIds,
        kind: dot.kind,
        requestStatus: dot.status,
        requestDate: dot.lessonDate,
        incoming: dot.incoming,
        personId,
      });
      return;
    }
    const requestId = dot.requestIds[0] || dot.requestId || requestIds[0];
    if (!requestId) return;
    setRadarRead({
      stage: "detail",
      requestId,
      fromList: false,
      requestIds,
      kind: dot.kind,
      requestStatus: dot.status,
      requestDate: dot.lessonDate,
      incoming: dot.incoming,
      personId,
    });
  }

  function openRadarRequest(
    id: string,
    kind?: RadarRead["kind"],
    meta?: { status?: string | null; date?: string | null }
  ) {
    if (!radarRead) return;
    const nextKind = kind || radarRead.kind;
    setRadarRead({
      stage: "detail",
      requestId: id,
      fromList: radarRead.stage === "list",
      requestIds: radarRead.requestIds,
      kind: nextKind,
      requestStatus: meta ? (meta.status ?? null) : radarRead.requestStatus,
      requestDate: meta ? (meta.date ?? null) : radarRead.requestDate,
      incoming: nextKind === "incoming",
      personId: radarRead.personId,
    });
  }

  function openAssign(lessonId: string, assignedTeacherName?: string | null) {
    setRadarRead((prev) =>
      prev?.requestId
        ? {
            ...prev,
            assignLessonId: lessonId,
            assignSubstitute: assignedTeacherName ?? null,
          }
        : prev
    );
  }

  function radarBack() {
    if (radarMessage) {
      setRadarMessage(false);
      return;
    }
    if (radarRead?.assignLessonId) {
      setRadarRead({
        ...radarRead,
        assignLessonId: null,
        assignSubstitute: null,
      });
      return;
    }
    if (stornoBack.current) {
      const abort = stornoBack.current;
      stornoBack.current = null;
      abort();
      return;
    }
    if ((role === "teacher" || role === "admin") && radarRead?.stage === "detail") {
      setRadarRead({
        ...radarRead,
        stage: "deck",
        requestId: null,
        fromList: false,
      });
      return;
    }
    if (radarRead?.stage === "deck") {
      setRadarRead(null);
      return;
    }
    if (radarRead?.stage === "detail" && radarRead.fromList) {
      setRadarRead({
        stage: "list",
        requestId: null,
        fromList: false,
        requestIds: radarRead.requestIds,
        kind: radarRead.kind,
        requestStatus: radarRead.requestStatus,
        requestDate: radarRead.requestDate,
        incoming: radarRead.incoming,
      });
      return;
    }
    if (radarRead) {
      setRadarRead(null);
      return;
    }
    closeRadar();
  }

  const discBubbles =
    radarBubbles == null || radarVisibleIds == null
      ? []
      : radarBubbles.filter((bubble) => radarVisibleIds.includes(bubble.id));

  const messageBubble = radarBubbles?.find(
    (bubble) =>
      bubble.request_id === radarRead?.requestId ||
      bubble.request_ids?.includes(radarRead?.requestId || "")
  );

  return {
    radarRestoreView,
    radarBubbles,
    radarTimeDays,
    radarError,
    radarRead,
    radarMessage,
    discBubbles,
    teachersDeclined: messageBubble?.teachers_declined,
    teachersPending: messageBubble?.teachers_pending,
    setRadarVisibleIds,
    setRadarTimeDays,
    setRadarMessage,
    openRadar,
    closeRadar,
    openRadarDot,
    openRadarRequest,
    openAssign,
    setStornoBack,
    radarBack,
  };
}
